import { Link } from "@tanstack/react-router";

import CloudinaryImage from "@/components/cloudinary-image";
import { Button } from "@/components/ui/button";
import useAuth from "@/hooks/use-auth";
import type { UserType } from "@/store";

import {
  AddGameIcon,
  ArticlesIcon,
  BlogIcon,
  ChatIcon,
  ClaimBusinessIcon,
  DashboardIcon,
  DiscordIcon,
  ExploreBusinessIcon,
  ExternalIcon,
  FeedbackIcon,
  GiftCardIcon,
  MeetupIcon,
  NewsIcon,
  OperatorsIcon,
  PhoneIcon,
} from "./icons";

export interface MenuItemType {
  content: React.ReactElement;
  hide?: string;
  key: string;
}

interface MenuItemGroupWithPropsType {
  hide?: string;
  items: MenuItemType[];
}

export type MenuItemGroupType = MenuItemGroupWithPropsType | MenuItemType[];

const BUTTON_CLASSNAME =
  "flex items-center justify-start md:px-2.5 rounded-[10px] text-white text-14 h-11 w-full hover:bg-white/10 transition-colors gap-0";
const ICON_CLASSNAME = "flex justify-center mr-2.5 w-[26px]";

interface AddGameProps {
  userId?: string;
}

export function AddGame({ userId }: AddGameProps) {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href={process.env.SUGGEST_EDIT_URL + (userId ? `&user_id=${userId}` : "")}
    >
      <span className={ICON_CLASSNAME}>
        <AddGameIcon />
      </span>
      Add an attraction
    </a>
  );
}

export function Articles() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="/blog/articles"
      rel="noopener"
      target="_blank"
    >
      <span className={ICON_CLASSNAME}>
        <ArticlesIcon />
      </span>
      Articles
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function Blog() {
  return (
    <a className={BUTTON_CLASSNAME} href="/blog" rel="noopener" target="_blank">
      Blog
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function BlogHome() {
  return (
    <a className={BUTTON_CLASSNAME} href="/blog" rel="noopener" target="_blank">
      <span className={ICON_CLASSNAME}>
        <BlogIcon />
      </span>
      Blog
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function Call() {
  return (
    <a className={BUTTON_CLASSNAME} href="tel:+18339871999">
      <span className={ICON_CLASSNAME}>
        <PhoneIcon />
      </span>
      Call +1 (833) 987-1999
    </a>
  );
}

export function Chat() {
  const handleClick = () => {
    if (!window.Intercom) {
      return;
    }
    window.Intercom("show");
  };

  return (
    <Button className={BUTTON_CLASSNAME} onClick={handleClick}>
      <span className={ICON_CLASSNAME}>
        <ChatIcon />
      </span>
      Message the Morty team
    </Button>
  );
}

export function Cities() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="/cities"
      rel="noopener"
      target="_blank"
    >
      All cities
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function ClaimBusiness() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="https://link.morty.app/e/claim-your-business"
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className={ICON_CLASSNAME}>
        <ClaimBusinessIcon />
      </span>
      Claim your business
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function Dashboard() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="https://biz.morty.app/"
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className={ICON_CLASSNAME}>
        <DashboardIcon />
      </span>
      Log in to Business account
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function Discord() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="https://link.morty.app/e/mAHjtohl8Ab"
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className={ICON_CLASSNAME}>
        <DiscordIcon />
      </span>
      Join the community Discord
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function ExploreBusiness() {
  return (
    <a className={BUTTON_CLASSNAME} href="/s/morty-for-business">
      <span className={ICON_CLASSNAME}>
        <ExploreBusinessIcon />
      </span>
      Explore Morty for Business
    </a>
  );
}

export function Feedback() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="https://mortyapp.feedbear.com/boards/feedback"
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className={ICON_CLASSNAME}>
        <FeedbackIcon />
      </span>
      Provide feedback
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function GiftCard() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="https://morty.app/s/digital-gifting"
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className={ICON_CLASSNAME}>
        <GiftCardIcon />
      </span>
      Gift cards
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function Login() {
  const { login } = useAuth();

  return (
    <Button className={BUTTON_CLASSNAME} onClick={login}>
      Log in
    </Button>
  );
}

export function Logout() {
  const { logout } = useAuth();

  const handleClick = () => {
    if (window.Intercom) {
      window.Intercom("shutdown");
    }

    logout();
  };

  return (
    <Button className={BUTTON_CLASSNAME} onClick={handleClick}>
      Log out
    </Button>
  );
}

export function Meetup() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="https://link.morty.app/e/meetup-groups"
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className={ICON_CLASSNAME}>
        <MeetupIcon />
      </span>
      Join a meetup
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

interface MyProfileProps {
  user: UserType;
}

export function MyProfile({ user }: MyProfileProps) {
  return (
    <Link className={BUTTON_CLASSNAME} to={`/@${user.username}`}>
      <span className={ICON_CLASSNAME}>
        <CloudinaryImage
          className="box-[25px] rounded-full"
          rW={60}
          size={25}
          user={user}
        />
      </span>
      My profile
    </Link>
  );
}

export function News() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="/blog/news"
      rel="noopener"
      target="_blank"
    >
      <span className={ICON_CLASSNAME}>
        <NewsIcon />
      </span>
      News
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function Operators() {
  return (
    <a
      className={BUTTON_CLASSNAME}
      href="/blog/for-operators"
      rel="noopener"
      target="_blank"
    >
      <span className={ICON_CLASSNAME}>
        <OperatorsIcon />
      </span>
      For operators
      <ExternalIcon className="ml-[5px]" />
    </a>
  );
}

export function Signup() {
  const { signup } = useAuth();

  return (
    <Button className={BUTTON_CLASSNAME} onClick={signup}>
      Sign up
    </Button>
  );
}
